import React, { useState, memo } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Loader2, Save, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';

const STYLES = [
  {
    id: 'motivateur',
    emoji: '🌟',
    title_fr: 'Le Motivateur',
    title_en: 'The Motivator',
    subtitle_fr: 'Energique & Positif',
    subtitle_en: 'Energetic & Positive',
    desc_fr: 'Choisis ce ton si tu as besoin d\'un "cheerleader". Le coach celebrera tes victoires, restera toujours positif et t\'encouragera avec une grande énergie.',
    desc_en: 'Choose this tone if you need a cheerleader. Coach will celebrate your wins, stay positive and encourage you with high energy.',
    color: 'border-amber-400 bg-amber-400/10',
    activeColor: 'border-amber-400 bg-amber-400/20 ring-2 ring-amber-400/50',
  },
  {
    id: 'empathique',
    emoji: '🧘',
    title_fr: 'L\'Empathique',
    title_en: 'The Empath',
    subtitle_fr: 'Doux & Comprehensif',
    subtitle_en: 'Gentle & Understanding',
    desc_fr: 'Choisis ce ton si tu reagis mal a la pression. Le coach sera sans jugement, rassurant, et t\'aidera a te relever en douceur si tu fais un ecart.',
    desc_en: 'Choose this if you react badly to pressure. Coach will be non-judgmental, reassuring, and help you bounce back gently from setbacks.',
    color: 'border-blue-400 bg-blue-400/10',
    activeColor: 'border-blue-400 bg-blue-400/20 ring-2 ring-blue-400/50',
  },
  {
    id: 'commando',
    emoji: '⚔️',
    title_fr: 'Le Commando',
    title_en: 'The Commando',
    subtitle_fr: 'Direct & Strict — Standard Extra Miles',
    subtitle_en: 'Direct & Strict — Extra Miles Standard',
    desc_fr: 'Choisis ce ton si tu veux la verite brute. Zéro complaisance, pas d\'excuses. Le coach te parlera de discipline et te poussera a l\'extreme.',
    desc_en: 'Choose this if you want the raw truth. Zero BS, no excuses. Coach will talk discipline and push you to the extreme.',
    color: 'border-red-400 bg-red-400/10',
    activeColor: 'border-red-400 bg-red-400/20 ring-2 ring-red-400/50',
  },
];

const SettingsCommunicationStyle = memo(function SettingsCommunicationStyle() {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const fr = language === 'fr';
  const currentStyle = user?.communication_style || '';
  const [selected, setSelected] = useState(currentStyle || 'commando');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/users/profile', { communication_style: selected });
      updateUser({ ...user, communication_style: selected });
      toast.success(fr ? 'Ton du Coach mis a jour!' : 'Coach tone updated!');
    } catch {
      toast.error(fr ? 'Erreur' : 'Error');
    } finally {
      setSaving(false);
    }
  };

  const hasChanged = selected !== currentStyle && !(selected === 'commando' && !currentStyle);

  return (
    <Card className="bg-card border-border" data-testid="settings-communication-style">
      <CardHeader>
        <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
          <MessageCircle className="w-5 h-5 text-primary" />
          {fr ? 'Comment veux-tu que ton Coach te parle ?' : 'How should your Coach talk to you?'}
        </CardTitle>
        <CardDescription>
          {fr ? 'Change la personnalite de l\'Assistant YUMIX.' : 'Change the personality of YUMIX Assistant.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {STYLES.map((style) => {
          const isActive = selected === style.id;
          return (
            <button
              key={style.id}
              type="button"
              onClick={() => setSelected(style.id)}
              data-testid={`comm-style-${style.id}`}
              className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                isActive ? style.activeColor : style.color + ' hover:brightness-110'
              }`}
            >
              <div className="flex items-center gap-3 mb-1.5">
                <span className="text-2xl">{style.emoji}</span>
                <div>
                  <p className="font-heading text-sm uppercase tracking-tight text-foreground">
                    {fr ? style.title_fr : style.title_en}
                  </p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wide">
                    {fr ? style.subtitle_fr : style.subtitle_en}
                  </p>
                </div>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed pl-11">
                {fr ? style.desc_fr : style.desc_en}
              </p>
            </button>
          );
        })}

        {hasChanged && (
          <Button
            onClick={handleSave}
            disabled={saving}
            className="w-full bg-primary text-primary-foreground mt-2"
            data-testid="comm-style-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {fr ? 'Sauvegarder' : 'Save'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
});

export { STYLES as COMMUNICATION_STYLES };
export default SettingsCommunicationStyle;
